// Single source of truth for dashboard customization (used by Dashboard + Profile).

export const DASHBOARD_SECTIONS = [
  { key: 'visitor', label: 'Visitor Insights', defaultWidth: 'full' },
  { key: 'charts', label: 'Trends & Status Charts', defaultWidth: 'full' },
  { key: 'myPerformance', label: 'Performance', defaultWidth: 'full' },
  { key: 'tallyItems', label: 'Tally Items Management', defaultWidth: 'full' },
  { key: 'tallyLedger', label: 'Tally Ledger Management', defaultWidth: 'full' },
  { key: 'personDistribution', label: 'Person Distribution Charts', defaultWidth: 'full' },
  { key: 'recentRecords', label: 'Recent Records', defaultWidth: 'full' },
];

export const DASHBOARD_SECTION_KEYS = DASHBOARD_SECTIONS.map((s) => s.key);

// Individual charts. `group` is the actual parent grid (ordering is per group);
// `section` is the high-level section used to group the Profile editor.
export const DASHBOARD_CHARTS = [
  { key: 'visitor-creator', label: 'Visits by Creator (tab)', section: 'visitor', group: 'visitor-pies' },
  { key: 'visitor-salesperson', label: 'Visits by Salesperson (tab)', section: 'visitor', group: 'visitor-pies' },
  { key: 'visitor-daily', label: 'My Daily Visits', section: 'visitor', group: 'visitor-daily' },
  { key: 'do-trend', label: 'Delivery Orders Trend', section: 'charts', group: 'charts' },
  { key: 'do-aging', label: 'Pending Aging', section: 'charts', group: 'charts' },
  { key: 'do-top-items', label: 'Top Items by Value', section: 'charts', group: 'charts' },
  { key: 'qn-trend', label: 'Quotation New Trend', section: 'charts', group: 'charts' },
  { key: 'tq-trend', label: 'Tally Quotations Trend', section: 'charts', group: 'charts' },
  { key: 'items-trend', label: 'Tally Items Created', section: 'items', group: 'items-trend' },
  { key: 'items-assignment', label: 'Item Assignments', section: 'items', group: 'items-pies', defaultWidth: 'full' },
  { key: 'ledger-trend', label: 'Ledger Entries Created', section: 'ledger', group: 'ledger-trend' },
  { key: 'person-combined', label: 'Person Distribution', section: 'person', group: 'person-combined', defaultWidth: 'full' },
];

export const DASHBOARD_CHART_KEYS = DASHBOARD_CHARTS.map((c) => c.key);

// Section labels reused by the Profile chart editor.
export const CHART_SECTION_LABELS = {
  visitor: 'Visitor Insights',
  charts: 'Trends & Status Charts',
  items: 'Tally Items Management',
  ledger: 'Tally Ledger Management',
  person: 'Person Distribution Charts',
};

export const DEFAULT_DASHBOARD_PREFERENCES = {
  hidden: [],
  order: DASHBOARD_SECTION_KEYS,
  layout: {},
  ui: { density: 'comfortable', accent: '' },
  preset: 'custom',
  charts: { hidden: [], order: DASHBOARD_CHART_KEYS, layout: {} },
};

export const DASHBOARD_PRESETS = {
  default: {
    hidden: [],
    order: DASHBOARD_SECTION_KEYS,
    layout: {},
    ui: { density: 'comfortable', accent: '' },
    charts: { hidden: [], order: DASHBOARD_CHART_KEYS, layout: {} },
  },
  compact: {
    hidden: [],
    order: DASHBOARD_SECTION_KEYS,
    layout: Object.fromEntries(DASHBOARD_SECTION_KEYS.map((k) => [k, 'half'])),
    ui: { density: 'compact', accent: '' },
    charts: { hidden: [], order: DASHBOARD_CHART_KEYS, layout: {} },
  },
  focus: {
    hidden: ['personDistribution', 'tallyLedger', 'recentRecords'],
    order: ['myPerformance', 'visitor', 'charts', 'tallyItems', 'personDistribution', 'tallyLedger', 'recentRecords'],
    layout: {},
    ui: { density: 'compact', accent: '' },
    charts: {
      hidden: ['person-combined'],
      order: DASHBOARD_CHART_KEYS,
      layout: {},
    },
  },
};

// Merge a saved key list with the full catalog, inserting any missing keys at
// their catalog position (so newly added sections/charts don't jump to the end).
const mergeWithCatalog = (saved, catalogKeys) => {
  const list = Array.isArray(saved) ? saved.filter((k) => catalogKeys.includes(k)) : [];
  const merged = [...list];
  catalogKeys.forEach((key, idx) => {
    if (merged.includes(key)) return;
    let insertAt = merged.length;
    for (let j = idx + 1; j < catalogKeys.length; j += 1) {
      const pos = merged.indexOf(catalogKeys[j]);
      if (pos !== -1) {
        insertAt = pos;
        break;
      }
    }
    merged.splice(insertAt, 0, key);
  });
  return merged;
};

// Ensures every known section appears exactly once.
export const normalizeOrder = (saved) => mergeWithCatalog(saved, DASHBOARD_SECTION_KEYS);

// Ensures every known chart appears exactly once.
export const normalizeChartOrder = (saved) => mergeWithCatalog(saved, DASHBOARD_CHART_KEYS);

// Applies a named preset (returns a full prefs object).
export const applyDashboardPreset = (name) => {
  const preset = DASHBOARD_PRESETS[name];
  if (!preset) return null;
  return {
    hidden: [...preset.hidden],
    order: [...preset.order],
    layout: { ...preset.layout },
    ui: { ...preset.ui },
    preset: name,
    charts: {
      hidden: [...preset.charts.hidden],
      order: [...preset.charts.order],
      layout: { ...preset.charts.layout },
    },
  };
};
